import { boolCode } from '../../../lib/boolCode';
import type { FetchClient, RequestOptions } from '../../../lib/http/http.types';
import { toQueryString } from '../../../lib/url/urlUtils';
import type {
  FilterState,
  PaginationMeta,
  TableColumn,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import {
  appendFilterParams,
  resolveDbSortBy,
} from '../../../components/ui/tableTemplate/tableUtils';
import type {
  Payslip,
  PayslipClosure,
  PayslipDetail,
  PayslipDetailHeader,
  PayslipLine,
} from '../payslipTypes';
import PayslipEndpoints from './payslipEndpoints';

const str = (v: unknown): string => (v != null ? String(v).trim() : '');
const strOrNull = (v: unknown): string | null => {
  const s = str(v);
  return s !== '' ? s : null;
};
const numOrNull = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

/** Maps a raw backend row to a {@link Payslip}. */
function mapPayslipRow(raw: Record<string, unknown>): Payslip {
  const statut = str(raw.statut);
  return {
    hc_uuid_key: str(raw.hc_uuid_key),
    hc_uuid_key_contrat: str(raw.hc_uuid_key_contrat),
    mois: str(raw.mois),
    soldetout_compte: boolCode(raw.soldetout_compte) ?? 'N',
    montant: numOrNull(raw.montant),
    devise: strOrNull(raw.devise),
    date_debut: strOrNull(raw.date_debut) ?? '',
    date_fin: strOrNull(raw.date_fin) ?? '',
    paymentdate: strOrNull(raw.paymentdate),
    codeterrain: str(raw.codeterrain),
    terrain_libelle: strOrNull(raw.terrain_libelle),
    origin_field_bulletin: str(raw.origin_field_bulletin),
    origin_field_bulletin_libelle: strOrNull(raw.origin_field_bulletin_libelle),
    statut: (statut === 'P' || statut === 'M' ? statut : '') as PayslipClosure,
  };
}

/** Maps a raw backend header row (list-view shape + identity) to a header. */
function mapPayslipDetailHeader(raw: Record<string, unknown>): PayslipDetailHeader {
  return {
    ...mapPayslipRow(raw),
    code_staff: strOrNull(raw.code_staff),
    nom: strOrNull(raw.nom),
    prenom: strOrNull(raw.prenom),
    libfonction: strOrNull(raw.libfonction),
    niveau: strOrNull(raw.niveau),
    codeanalytique: strOrNull(raw.codeanalytique),
    lib_projet: strOrNull(raw.lib_projet),
    axeanalytique: strOrNull(raw.axeanalytique),
    lib_axeanalytique: strOrNull(raw.lib_axeanalytique),
    dateentree: strOrNull(raw.dateentree),
    datedeb: strOrNull(raw.datedeb),
    anciennete: numOrNull(raw.anciennete),
  };
}

/** Maps a raw backend `v_payslip_lines` row to a {@link PayslipLine}. */
function mapPayslipLine(raw: Record<string, unknown>): PayslipLine {
  return {
    hc_uuid_key: str(raw.hc_uuid_key),
    codeterrain: str(raw.codeterrain),
    code: strOrNull(raw.code),
    libelle: strOrNull(raw.libelle),
    type: strOrNull(raw.type),
    base_bulletin: numOrNull(raw.base_bulletin),
    base_date_bulletin: strOrNull(raw.base_date_bulletin),
    montant: numOrNull(raw.montant),
    numeroordre: numOrNull(raw.numeroordre),
    gain: strOrNull(raw.gain),
    retenue: strOrNull(raw.retenue),
    retenuepatronale: strOrNull(raw.retenuepatronale),
    soldetoutcompte: strOrNull(raw.soldetoutcompte),
    visible: strOrNull(raw.visible),
    commentaire: strOrNull(raw.commentaire),
    actif_bulletin: strOrNull(raw.actif_bulletin),
  };
}

/** Maps the raw detail payload `{ header, lines }` to a {@link PayslipDetail}. */
function mapPayslipDetail(raw: {
  header: Record<string, unknown>;
  lines?: Record<string, unknown>[];
}): PayslipDetail {
  return {
    header: mapPayslipDetailHeader(raw.header),
    lines: Array.isArray(raw.lines) ? raw.lines.map(mapPayslipLine) : [],
  };
}

export type GetPayslipsParams = {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: FilterState<Payslip>;
  columns?: TableColumn<Payslip>[];
  page?: number;
  limit?: number;
};

export type PayslipsResponse = { data: Payslip[]; meta: PaginationMeta };

export function PayslipApiFactory(client: FetchClient) {
  return {
    /**
     * Server-side sorted/filtered payslip list for an employee.
     * GET /employees/:employeeId/payslips
     */
    getAllByEmployeeId: async (
      employeeId: string,
      params: GetPayslipsParams = {},
      opts?: RequestOptions,
    ): Promise<PayslipsResponse> => {
      const qs = new URLSearchParams();
      const columns = params.columns ?? [];

      if (params.sortBy) {
        qs.set(PayslipEndpoints.PARAMS.SORT_BY, resolveDbSortBy(params.sortBy, columns));
      }
      if (params.sortOrder) qs.set(PayslipEndpoints.PARAMS.SORT_ORDER, params.sortOrder);
      if (params.filters && params.columns) {
        appendFilterParams(qs, params.filters, columns);
      }
      if (params.page != null) qs.set(PayslipEndpoints.PARAMS.PAGE, String(params.page));
      if (params.limit != null) qs.set(PayslipEndpoints.PARAMS.LIMIT, String(params.limit));

      const query = toQueryString(qs);
      const response = await client.get<{
        data: Record<string, unknown>[];
        meta?: PaginationMeta;
      }>(`${PayslipEndpoints.LIST(employeeId)}${query ? `?${query}` : ''}`, opts);

      const data = Array.isArray(response?.data) ? response.data.map(mapPayslipRow) : [];
      const meta: PaginationMeta = response?.meta ?? {
        total: data.length,
        page: params.page ?? 1,
        limit: params.limit ?? data.length,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      };
      return { data, meta };
    },

    /**
     * One payslip's detail (frozen header + ordered rubrique lines).
     * GET /employees/:employeeId/payslips/:payslipId
     */
    getById: async (
      employeeId: string,
      payslipId: string,
      opts?: RequestOptions,
    ): Promise<PayslipDetail> => {
      const raw = await client.get<{
        header: Record<string, unknown>;
        lines?: Record<string, unknown>[];
      }>(PayslipEndpoints.DETAIL(employeeId, payslipId), opts);
      return mapPayslipDetail(raw);
    },
  };
}
